import axios from 'axios';
import { REACT_APP_TEAMCAMP_APIURL } from '../global/environment';
import { captureException } from './logService';
import { getAPIErrorReason } from '../helpers/common';
import { rootStore } from '../mobx/rootStore';
import { VercelProject } from '../mobx/interfaces/project';

interface VercelIntegrationPayload {
  code: string;
  teamId: string;
  configurationId: string;
}

/**
 * @desc vercel auth
 * @param {*}
 */
export const vercelIntegration = async (payload: VercelIntegrationPayload) => {
  try {
    rootStore.settingStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/vercel/auth`, payload);
    if (response) return true;
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to- vercel integration! please try again';
    captureException(error, 'settings');
    dispatchVercelError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc get dynamic vercel account
 * @param {*}
 */
export const getVercelAccount = async () => {
  try {
    rootStore.settingStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/vercel/accounts`);
    const { data } = response;
    if (data) {
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get vercel account please try again';
    captureException(error);
    dispatchVercelError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc delete vercel account
 * @param {string} vercelId - ID of the task group to delete
 */
export const deleteVercelAccount = async (vercelId: string | null) => {
  try {
    if (!vercelId) return false;
    rootStore.settingStore.setLoading(true);
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/vercel/accounts/${vercelId}/remove`);
    if (response) {
      dispatchTaskSuccess('Vercel account deleted successfully');
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete vercel account please try again';
    captureException(error);
    dispatchVercelError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Get Vercel Project list
 * @param {*}
 */
export const getVercelProjectList = async () => {
  try {
    rootStore.settingStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/vercel/projects`);
    if (response) {
      return response.data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get vercel project list! please try again';
    captureException(error);
    dispatchVercelError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Get Vercel Project list
 * @param {*}
 */
export const setVercelProject = async (projectID: string | undefined, payload: VercelProject) => {
  try {
    if (!projectID) return false;
    rootStore.settingStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/vercel/projects/${projectID}`, payload);
    if (response) {
      return response;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to set vercel project! please try again';
    captureException(error);
    dispatchVercelError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Get Task Vercel Deployment
 * @param {string} projectId - ID of the project whose task branch is to deploy
 * @param {string} taskId - ID of the task which vercel deployment to get
 */
export const getVercelDeployment = async (projectID: string | undefined, taskId: string | undefined) => {
  try {
    if (!projectID || !taskId) return false;
    rootStore.settingStore.setLoading(true);
    const response = await axios.get(
      `${REACT_APP_TEAMCAMP_APIURL}/vercel/projects/${projectID}/task/${taskId}/deployments`
    );
    const { data } = response;
    if (data) {
      // Route to the requested task so a late response can't overwrite another task's deployments.
      rootStore.taskStore.getTaskById(taskId)?.setVercelDeployment(data);
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get vercel branch deployment! please try again';
    captureException(error);
    dispatchVercelError(error);
    return false;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

/**
 * @desc Get Task Vercel Deployment
 * @param {string} payload - projectId and branchName
 */
export const createBranchDeployment = async (payload: {
  projectId: string | undefined;
  branchName: string | undefined;
}) => {
  try {
    if (!payload.projectId || !payload.branchName) return undefined;
    rootStore.settingStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/vercel/create-deploy`, payload);
    if (response) {
      return response;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to set vercel project! please try again';
    captureException(error);
    dispatchVercelError(error);
    return undefined;
  } finally {
    rootStore.settingStore.setLoading(false);
  }
};

function dispatchVercelError(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}

function dispatchTaskSuccess(msg: string) {
  rootStore.alertNotificationStore.setSuccessMessage(msg);
}
